'use client';
import { useState } from 'react';
import type { Metadata } from 'next';
import PageContainer from '@/layout/page-container';
import Section from '@/layout/section';
import { PageHero, Callout } from '@/modules/marketing/components/page-shared';

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // React clears `currentTarget` once the handler yields, so hold a reference
    // to the form element for the reset that happens after the await below.
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.get('firstName'),
          lastName: formData.get('lastName'),
          email: formData.get('email'),
          phone: formData.get('phone'),
          subject: formData.get('subject'),
          message: formData.get('message'),
        }),
      });
      
      if (response.ok) {
        setSuccess(true);
        form.reset();
        setTimeout(() => setSuccess(false), 5000);
      } else {
        // Surface the failure instead of leaving the button to settle back to
        // its idle label, which read as the form doing nothing at all.
        setError(
          "We couldn't send your message. Please try again, or email us directly at support@acerotations.com."
        );
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(
        "We couldn't send your message. Please check your connection and try again, or email us directly at support@acerotations.com."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageContainer>
      <PageHero
        kicker="Connect"
        title="Contact Us"
        subtitle="Support, placement guidance, and partnership discussions. We aim to respond within 1–2 business days." />
      <Section>
        <div className="space-y-16">
          <div className="flex justify-center">
            <form onSubmit={handleSubmit} className="w-full max-w-2xl space-y-4 bg-white border border-[var(--brand-border)] rounded-xl p-6 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wide">First Name</label>
                  <input name="firstName" required className="h-10 w-full rounded-md border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wide">Last Name</label>
                  <input name="lastName" required className="h-10 w-full rounded-md border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wide">Email</label>
                <input name="email" type="email" required className="h-10 w-full rounded-md border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wide">Phone Number</label>
                <input name="phone" type="tel" className="h-10 w-full rounded-md border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wide">Subject</label>
                <input name="subject" required className="h-10 w-full rounded-md border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wide">Message</label>
                <textarea name="message" required rows={6} className="w-full rounded-md border border-[var(--brand-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
              </div>
              {error && (
                <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                  {error}
                </div>
              )}
              {success && (
                <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-md p-3">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-[10px] text-[var(--brand-muted)] uppercase tracking-wide">We respect your privacy.</div>
                <button type="submit" disabled={loading} className="h-10 px-5 rounded-md bg-[var(--brand-primary)] text-white text-sm font-medium hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Section>
    </PageContainer>
  );
}
